import React, { useState, useEffect } from 'react';
import { useToast } from '../../components/Toast';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider';
import Header from '../../components/Header';
import { styles, colors, radius, shadows } from '../../styles/theme';
import { invalidateQueries } from '../../hooks/useCachedQuery';
import { logError } from '../../utils/logError';
import { ciftGirisOnayMetni } from '../../utils/ciftGiris';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const getAuthHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

const MalKabulPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id: siparisId } = useParams();

  const toast = useToast();
  const [siparis, setSiparis] = useState(null);
  const [teslimatListesi, setTeslimatListesi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [genelNot, setGenelNot] = useState('');

  // Kalite Kontrol Formu
  const [qcForm, setQcForm] = useState({
    sicaklik: '',
    ambalaj: false,
    etiket: false,
    açıklama: ''
  });

  useEffect(() => {
    if (!siparisId) {
      navigate('/siparisler');
      return;
    }

    fetch(`${API_URL}/api/siparisler/${siparisId}`, { headers: getAuthHeaders() })
      .then(res => res.json())
      .then(data => {
        setSiparis(data);
        const initialList = data.kalemler.map(k => ({
          siparis_kalem_id: k.id,
          malzeme_id: k.malzeme_id,
          malzeme_ad: k.malzeme_ad,
          malzeme_birim: k.malzeme_birim,
          siparis_miktar: k.miktar,
          teslim_miktar: k.miktar,
          durum: 'OK',
          not_kalem: '',
          son_kullanma_tarihi: '',
          uretim_tarihi: ''
        }));
        setTeslimatListesi(initialList);
        setLoading(false);
      })
      .catch(err => {
        logError(err);
        toast.error('Sipariş yüklenirken hata oluştu');
        navigate('/siparisler');
      });
  }, [siparisId, navigate, toast]);

  const handleMiktarChange = (index, val) => {
    const newList = [...teslimatListesi];
    const miktar = parseFloat(val) || 0;
    newList[index].teslim_miktar = miktar;

    const fark = miktar - newList[index].siparis_miktar;
    if (fark < -0.01) newList[index].durum = 'EKSIK';
    else if (fark > 0.01) newList[index].durum = 'FAZLA';
    else newList[index].durum = 'OK';

    setTeslimatListesi(newList);
  };

  const handleDurumChange = (index, val) => {
    const newList = [...teslimatListesi];
    newList[index].durum = val;
    setTeslimatListesi(newList);
  };

  const handleTarihChange = (index, field, val) => {
    const newList = [...teslimatListesi];
    newList[index][field] = val;
    setTeslimatListesi(newList);
  };

  const handleSubmit = async () => {
    if (!qcForm.sicaklik) {
      toast.warning("Lütfen sıcaklık değerini giriniz.");
      return;
    }
    if (!qcForm.ambalaj) {
      toast.warning("Lütfen ambalaj kontrolünü onaylayınız.");
      return;
    }
    if (!qcForm.etiket) {
      toast.warning("Lütfen etiket kontrolünü onaylayınız.");
      return;
    }

    setSubmitting(true);

    try {
      const grnData = {
        siparis_id: siparis.id,
        teslim_alan_id: user?.id || 1,
        lokasyon_id: 1,
        teslimat_notu: genelNot,
        kalemler: teslimatListesi.map(t => ({
          siparis_kalem_id: t.siparis_kalem_id,
          malzeme_id: t.malzeme_id,
          siparis_miktar: t.siparis_miktar,
          teslim_miktar: t.teslim_miktar,
          durum: t.durum,
          not_kalem: t.not_kalem,
          son_kullanma_tarihi: t.son_kullanma_tarihi || null,
          uretim_tarihi: t.uretim_tarihi || null
        }))
      };

      const res1 = await fetch(`${API_URL}/api/grn`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(grnData)
      });

      if (!res1.ok) throw new Error('GRN olusturulamadi');
      const grnResult = await res1.json();

      const kkData = {
        kontrolor_id: user?.id || 1,
        sonuc: 'ONAYLANDI',
        açıklama: qcForm.açıklama || 'Manuel Kontrol Yapildi',
        sicaklik_ok: parseFloat(qcForm.sicaklik) <= 4,
        sicaklik_c: parseFloat(qcForm.sicaklik),
        ambalaj_ok: qcForm.ambalaj,
        etiket_ok: qcForm.etiket
      };

      await fetch(`${API_URL}/api/grn/${grnResult.id}/kalite-kontrol`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(kkData)
      });

      const onayla = (force) =>
        fetch(`${API_URL}/api/grn/${grnResult.id}/approve${force ? '?onayla=true' : ''}`, {
          method: 'PATCH',
          headers: getAuthHeaders()
        });

      const basariliBitir = () => {
        invalidateQueries(['grn', 'stok', 'dashboard', 'siparis']);
        toast.success('Mal kabul başarıyla tamamlandı! Stoklar güncellendi ve maliyetler işlendi.');
        navigate('/siparisler');
      };

      const res3 = await onayla(false);
      const data3 = await res3.json().catch(() => ({}));

      if (data3 && data3.onay_gerekli) {
        const onay = window.confirm(
          `${data3.mesaj || 'Olası çift giriş — çift stok riski.'}\n\n${ciftGirisOnayMetni(data3.cakismalar)}\n\nYine de onayla?`
        );
        if (!onay) {
          toast.info('Mal kabul onaylanmadı. GRN beklemede kaldı.');
          return;
        }
        const res4 = await onayla(true);
        if (res4.ok) {
          basariliBitir();
        } else {
          toast.warning('GRN oluşturuldu ama onaylanamadı. Lütfen sistem yöneticisine başvurun.');
        }
      } else if (res3.ok) {
        basariliBitir();
      } else {
        toast.warning('GRN oluşturuldu ama onaylanamadı. Lütfen sistem yöneticisine başvurun.');
      }
    } catch (err) {
      logError(err);
      toast.error('İşlem sırasında hata oluştu: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading && !siparis) return <div>Yükleniyor...</div>;

  return (
    <div>
      <Header title={`Mal Kabul: ${siparis.siparis_no}`} />
      <div style={{ padding: '20px', background: colors.background, minHeight: 'calc(100vh - 60px)' }}>
        <div style={{ background: colors.surface, padding: '20px', borderRadius: radius.md, marginBottom: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
            <div>
              <strong>Tedarikçi:</strong> {siparis.tedarikci_ad} <br />
              <strong>Sipariş Tarihi:</strong> {new Date(siparis.siparis_tarihi).toLocaleDateString()}
            </div>
            <div style={{ textAlign: 'right' }}>
              <button onClick={() => navigate('/siparisler')} style={{ background: '#e0e0e0', color: '#333', border: 'none', padding: '8px 15px', borderRadius: radius.sm, cursor: 'pointer' }}>iptal</button>
            </div>
          </div>

          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: colors.backgroundAlt, borderBottom: '2px solid #ddd' }}>
                <th style={{ padding: '7px', textAlign: 'left' }}>Malzeme</th>
                <th style={{ padding: '7px', textAlign: 'right' }}>Sipariş Miktar</th>
                <th style={{ padding: '7px', textAlign: 'right', width: '150px' }}>Teslim Alinan</th>
                <th style={{ padding: '7px', textAlign: 'left', paddingLeft: '20px' }}>Fark</th>
                <th style={{ padding: '7px', textAlign: 'left' }}>Durum</th>
                <th style={{ padding: '7px', textAlign: 'left' }}>SKT</th>
                <th style={{ padding: '7px', textAlign: 'left' }}>Üretim Tarihi</th>
              </tr>
            </thead>
            <tbody>
              {teslimatListesi.map((item, idx) => {
                const fark = item.teslim_miktar - item.siparis_miktar;
                return (
                  <tr key={item.siparis_kalem_id} style={{ borderBottom: `1px solid ${colors.border}` }}>
                    <td style={{ padding: '10px' }}>
                      <strong>{item.malzeme_ad}</strong><br />
                      <span style={{ fontSize: '12px', color: colors.textSecondary }}>{item.malzeme_birim}</span>
                    </td>
                    <td style={{ padding: '7px', textAlign: 'right' }}>{item.siparis_miktar}</td>
                    <td style={{ padding: '10px' }}>
                      <input
                        type="number"
                        value={item.teslim_miktar}
                        onChange={(e) => handleMiktarChange(idx, e.target.value)}
                        style={{ width: '100%', padding: '8px', border: '2px solid #ddd', borderRadius: radius.sm, textAlign: 'right', fontWeight: 'bold' }}
                      />
                    </td>
                    <td style={{ padding: '10px', paddingLeft: '20px', color: fark < -0.01 ? 'red' : (fark > 0.01 ? 'green' : '#ccc'), fontWeight: fark !== 0 ? 'bold' : 'normal' }}>
                      {fark > 0 ? '+' : ''}{fark.toFixed(2)} {item.malzeme_birim}
                    </td>
                    <td style={{ padding: '10px' }}>
                      <select
                        value={item.durum}
                        onChange={(e) => handleDurumChange(idx, e.target.value)}
                        style={{ padding: '5px', borderRadius: radius.sm, border: item.durum === 'OK' ? `1px solid ${colors.border}` : '1px solid red', background: item.durum === 'OK' ? 'white' : '#fff3cd' }}
                      >
                        <option value="OK">Tamam</option>
                        <option value="EKSIK">Eksik</option>
                        <option value="FAZLA">Fazla</option>
                        <option value="HASARLI">Hasarli</option>
                      </select>
                    </td>
                    <td style={{ padding: '10px' }}>
                      <input
                        type="date"
                        value={item.son_kullanma_tarihi}
                        onChange={(e) => handleTarihChange(idx, 'son_kullanma_tarihi', e.target.value)}
                        title="Son kullanma tarihi (opsiyonel)"
                        style={{ padding: '6px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '13px' }}
                      />
                    </td>
                    <td style={{ padding: '10px' }}>
                      <input
                        type="date"
                        value={item.uretim_tarihi}
                        onChange={(e) => handleTarihChange(idx, 'uretim_tarihi', e.target.value)}
                        title="Üretim tarihi (opsiyonel)"
                        style={{ padding: '6px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '13px' }}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <div style={{ marginTop: '20px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Teslimat Notu:</label>
            <textarea
              value={genelNot}
              onChange={(e) => setGenelNot(e.target.value)}
              style={{ width: '100%', height: '80px', padding: '10px', border: `1px solid ${colors.border}`, borderRadius: radius.sm }}
              placeholder="Irsaliye no, arac plakasi vb. notlar..."
            />
          </div>

          <div style={{ marginTop: '20px', padding: '20px', background: '#e9ecef', borderRadius: radius.md, border: '1px solid #ced4da' }}>
            <h4 style={{ margin: '0 0 15px 0', color: styles.secondary }}>Kalite Kontrol (QC)</h4>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Sicaklik (C) <span style={{ color: 'red' }}>*</span></label>
                <input
                  type="number"
                  step="0.1"
                  value={qcForm.sicaklik}
                  onChange={(e) => setQcForm({ ...qcForm, sicaklik: e.target.value })}
                  placeholder="Orn: 3.5"
                  style={{ width: '100%', padding: '10px', border: '1px solid #ced4da', borderRadius: radius.sm }}
                />
                <small style={{ display: 'block', marginTop: '5px', color: colors.textSecondary }}>Maksimum 4.0 C olmalidir.</small>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="checkbox"
                    id="chkAmbalaj"
                    checked={qcForm.ambalaj}
                    onChange={(e) => setQcForm({ ...qcForm, ambalaj: e.target.checked })}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  <label htmlFor="chkAmbalaj" style={{ cursor: 'pointer' }}>Ambalajlar saglam ve temiz</label>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="checkbox"
                    id="chkEtiket"
                    checked={qcForm.etiket}
                    onChange={(e) => setQcForm({ ...qcForm, etiket: e.target.checked })}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  <label htmlFor="chkEtiket" style={{ cursor: 'pointer' }}>Etiket ve SKT kontrolu yapildi</label>
                </div>
              </div>
            </div>

            <div style={{ marginTop: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Kontrolor Notu:</label>
              <textarea
                value={qcForm.açıklama}
                onChange={(e) => setQcForm({ ...qcForm, açıklama: e.target.value })}
                style={{ width: '100%', height: '60px', padding: '10px', border: '1px solid #ced4da', borderRadius: radius.sm }}
                placeholder="Kalite kontrol ile ilgili notlar..."
              />
            </div>
          </div>

          <div style={{ marginTop: '30px', display: 'flex', gap: '10px' }}>
            <button
              onClick={handleSubmit}
              disabled={submitting}
              style={{
                flex: 1,
                padding: '15px',
                background: styles.success,
                color: 'white',
                border: 'none',
                borderRadius: radius.sm,
                fontSize: '18px',
                fontWeight: 'bold',
                cursor: submitting ? 'wait' : 'pointer',
                boxShadow: shadows.md
              }}
            >
              {submitting ? 'Isleniyor...' : 'MAL KABULU ONAYLA ve STOGA AL'}
            </button>
          </div>

        </div>
      </div>
    </div>
  );
};

export default MalKabulPage;
